/**
 * The document panel on a product — the sibling of ProductImages, for the files
 * that are read rather than looked at.
 *
 * These are not decoration either: an injected TecDoc article carries them in its
 * `pdfs[]`, in the same shape and under the same category labels the source uses
 * for its own, so what is uploaded here is what a B2B customer can download. The
 * category picker offers TecDoc's own vocabulary for exactly that reason — a
 * consumer grouping "Istruzioni montaggio" groups ours into the same heading
 * without knowing they are ours.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useApi } from '../lib/api';
import type { ProductDocument } from '../lib/types';
import { Empty, ErrorBox, Loading, Switch, cx } from '../ui';

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/** TecDoc's own document categories. Kept in sync with DOC_CATEGORIES on the API. */
const CATEGORIES = [
  'Informazione assistenza tecnica',
  'Istruzioni montaggio',
  'Informazioni sul prodotto',
  'Certificato dei dati di sicurezza',
  'Opuscoli',
  'Info montaggio',
  'Disegno tecnico',
  'Certificato di garanzia',
];

export default function ProductDocuments({ productId }: { productId: number }) {
  const { request, base } = useApi();
  const [items, setItems] = useState<ProductDocument[] | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [category, setCategory] = useState(CATEGORIES[2]!);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    const r = await request<{ items: ProductDocument[] }>('GET', `/v1/products/${productId}/documents`);
    if (!r.ok) {
      setError(typeof r.data === 'object' && r.data && 'error' in r.data ? String(r.data.error) : `HTTP ${r.status}`);
      setItems([]);
      return;
    }
    setError('');
    setItems(r.data.items ?? []);
  }, [productId, request]);

  useEffect(() => {
    void load();
  }, [load]);

  async function upload(files: FileList | null) {
    if (!files || files.length === 0) return;
    setBusy(true);
    const form = new FormData();
    for (const f of Array.from(files)) form.append('file', f);
    form.append('headerDescription', category);
    const r = await request<{ added: number; errors?: { file: string; error: string }[] }>(
      'POST',
      `/v1/admin/products/${productId}/documents`,
      form,
    );
    setBusy(false);
    if (fileRef.current) fileRef.current.value = '';
    if (!r.ok) {
      setError(`upload failed (HTTP ${r.status})`);
      return;
    }
    const failed = r.data.errors ?? [];
    setError(failed.length ? failed.map((f) => `${f.file}: ${f.error}`).join('; ') : '');
    await load();
  }

  async function patch(id: number, body: Record<string, unknown>) {
    await request('PATCH', `/v1/admin/documents/${id}`, body);
    await load();
  }

  async function remove(id: number) {
    await request('DELETE', `/v1/admin/documents/${id}`);
    await load();
  }

  async function move(index: number, delta: number) {
    if (!items) return;
    const next = [...items];
    const to = index + delta;
    if (to < 0 || to >= next.length) return;
    [next[index], next[to]] = [next[to]!, next[index]!];
    setItems(next);
    await request('PUT', `/v1/admin/products/${productId}/documents/order`, { ids: next.map((d) => d.id) });
    await load();
  }

  // Content-addressed and public, so no token on the URL. The API returns an
  // absolute one — the CDN when a pull zone is serving, our own origin otherwise.
  const href = (doc: ProductDocument): string =>
    doc.url ??
    `${base.replace(/\/$/, '')}/docs/${doc.sha256.slice(0, 2)}/${doc.sha256.slice(2, 4)}/${doc.sha256}.${doc.ext}`;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={fileRef}
          type="file"
          accept="application/pdf"
          multiple
          className="input"
          style={{ width: 'auto' }}
          onChange={(e) => void upload(e.target.files)}
          disabled={busy}
        />
        <select
          className="input"
          style={{ width: 'auto' }}
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          title="The category new uploads are filed under"
        >
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        {busy && <span className="text-[12px] text-muted">uploading…</span>}
        <span className="ml-auto text-[12px] text-faint">published in the article&rsquo;s `pdfs[]`</span>
      </div>

      {error && <ErrorBox>{error}</ErrorBox>}

      {items === null ? (
        <Loading />
      ) : items.length === 0 ? (
        <Empty>No documents. Upload a datasheet, a fitting instruction or a safety sheet.</Empty>
      ) : (
        <div className="flex flex-col gap-2">
          {items.map((doc, i) => (
            <div
              key={doc.id}
              className={cx(
                'flex flex-wrap items-center gap-2 rounded border border-line bg-panel2 p-2',
                !doc.injected && 'opacity-60',
              )}
            >
              <span className="chip border-line">PDF</span>
              <a href={href(doc)} target="_blank" rel="noreferrer" className="mono truncate text-[12px]" style={{ maxWidth: 260 }}>
                {doc.fileName ?? `${doc.sha256.slice(0, 10)}.pdf`}
              </a>
              <select
                className="input text-[12px]"
                style={{ width: 'auto' }}
                value={doc.headerDescription}
                onChange={(e) => void patch(doc.id, { headerDescription: e.target.value })}
              >
                {(CATEGORIES.includes(doc.headerDescription) ? CATEGORIES : [doc.headerDescription, ...CATEGORIES]).map(
                  (c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ),
                )}
              </select>
              <input
                className="input text-[12px]"
                style={{ width: 160 }}
                placeholder="Title…"
                defaultValue={doc.title ?? ''}
                onBlur={(e) => {
                  if (e.target.value !== (doc.title ?? '')) void patch(doc.id, { title: e.target.value || null });
                }}
              />
              <span className="text-[11px] text-faint">
                {fmtBytes(doc.bytes)} · {doc.origin}
                {doc.lang ? ` · ${doc.lang}` : ''}
              </span>
              <div className="ml-auto flex items-center gap-1">
                <button className="btn btn-sm" disabled={i === 0} onClick={() => void move(i, -1)} title="Move earlier">
                  ←
                </button>
                <button
                  className="btn btn-sm"
                  disabled={i === items.length - 1}
                  onClick={() => void move(i, 1)}
                  title="Move later"
                >
                  →
                </button>
                <Switch on={doc.injected} onChange={(v) => void patch(doc.id, { injected: v })}>
                  <span className="text-[11px]">inject</span>
                </Switch>
                <button className="btn btn-sm btn-danger" onClick={() => void remove(doc.id)}>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
